// Restored verbatim from src/pages/RFQ/RFQ/Views/ListView/RFQListView.js
// @ 310ebe2d4^:src/pages/RFQ/RFQ/Views/ListView/RFQListView.js (TASK-027)
//
// Adapter-layer changes (see TASK_SUMMARY-027.md for the full diff report):
//  - `useSelector(state.rfq.rfqs...)` redux reads replaced with `rows`/
//    `isLoading` props, mirroring `RFQGridView`'s adaptation.
//  - `@reach/router` `navigate` swapped for `useRfxNavigate` (`newui/` bans
//    direct `@reach/router` imports).
//  - Row-level `EditDropdown` action menu + its own extend-deadline modal/
//    redux dispatch replaced with TASK-018's `RfxRowActions` (status-driven)
//    plus a single shared extend modal owned by the parent list — same slot
//    as before (right-most column). ESCALATION: same action-affordance
//    placement conflict flagged for `RFQCard` (brief item 7) applies here.
//  - `data-testid={`rfx-row-${rfq?.id}`}` added to each `TableRow` —
//    attribute-only, for the TASK-017 search / row-target testid contract.
//  - TASK-027 (this task, second pass): same Panel/Approvals sharing as
//    `RFQCard.js` — see its header comment for the full rationale. Mirrors
//    both conditionals here: `rfq?.linkOverride` short-circuits the owner
//    edit/view routing (used by both the row `onClick` and the cell link
//    target), and `rfq?.supplierInvited` gates the action-menu cell.
import { Flex, VStack } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { Avatar } from "components/RFQ/Avatar";
import GlobalAvatarGroup from "components/RFQ/AvatarGroup/AvatarGroup";
import { Button } from "newui/components/Button";
import NotFoundDatas from "components/RFQ/NotFoundDatas/NotFoundDatas";
import { ProgressCircular } from "components/RFQ/ProgressIndicator";
import StatusBadge from "components/RFQ/StatusBadge/StatusBadge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "components/RFQ/Table";
import dayjs from "newui/utils/dayjs";
import tw, { styled, css } from "twin.macro";
import { timezoneChecker } from "utils/helpers";
import { getSlug } from "helpers/linkHelper";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";
import { RFX_STATUS, RFX_TABS } from "utils/constants";
import UnreadMessage from "newui/components/UnreadMessage";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useRfxNavigate } from "app/useRfxNavigate";
import { RfxRowActions } from "../TendersQuotes/RfxRowActions";

const ListViewStyled = styled.div(() => [
	tw`py-8`,
	css`
		.title {
			font-weight: 500;
			font-family: Roboto;
			font-size: 14px;
		}

		.text {
			font-weight: 400;
			font-family: Roboto;
			font-size: 14px;
		}
	`,
]);

const SkeletonCustom = styled.div(() => [
	tw`animate-pulse bg-gray-300 w-full h-6`,
]);

const SkeleteonRow = () => (
	<TableRow>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
	</TableRow>
);

// TASK-036 (typecheck sweep): `currentTab` defaulted — see RFQGridView.js note.
const RFQListView = ({ rows, isLoading, currentTab = "", onExtendClick }) => {
	const { clientPaths } = useAuthContext();
	const rfqsData = rows;
	const navigate = useRfxNavigate();
	const currPage = 1;
	const totalUser = (suppliers) => {
		const all = suppliers?.all?.length || 0;
		const accepted = suppliers?.accepted?.length;
		const submitted = suppliers?.submitted?.length;
		return { all, accepted, submitted };
	};
	const onOpen = () => {};
	const setModalType = () => {};

	return (
		<ListViewStyled>
			<div className="card-shadow rounded-md">
				<Table containerClass={`w-full border rounded-md`}>
					<TableHeader>
						<TableRow>
							<TableHead firstCol={true}>
								<div
									className="flex"
									style={{ gap: "8px", alignItems: "center" }}
								>
									Project
								</div>
							</TableHead>
							<TableHead className={tw`max-w-[280px]`}>
								Project Description
							</TableHead>
							<TableHead className={tw`max-w-[218px]`}>Status</TableHead>
							<TableHead className={tw`max-w-[218px]`}>
								Submission Deadline
							</TableHead>
							<TableHead className={tw`max-w-[250px]`}>Company</TableHead>
							<TableHead className={tw`max-w-[108px]`}>Accepted</TableHead>
							<TableHead className={tw`max-w-[108px]`}>Submitted</TableHead>
							<TableHead className={tw`max-w-[68px]`}>
								<div className="hidden">Icon</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading && currPage === 1 && !rfqsData ? (
							[...Array(4)].map(() => <SkeleteonRow />)
						) : rfqsData?.length > 0 ? (
							<>
								{rfqsData?.length > 0 &&
									rfqsData.map((rfq) => {
										const slug = getSlug(rfq?.name);
										const { all, accepted, submitted } = totalUser(
											rfq?.supplierInvited
										);
										// TASK-027 Panel/Approvals adapter: `rfq.linkOverride`, when set
										// by PanelList/ApprovalsList (never by T&Q), takes the
										// scope-correct target instead of the owner edit/view
										// routing below — see RFQCard.js's matching comment.
										const linkUrl =
											rfq?.linkOverride ??
											(isRfxStatusMatch(rfq?.status, RFX_STATUS.DRAFT)
												? clientPaths.editRfx(rfq?.id)
												: clientPaths.viewRfx(rfq));
										return (
											<TableRow
												key={rfq?.id}
												className="cursor-pointer hover:bg-utility-blue-light-50/30"
												onClick={() => navigate(linkUrl)}
												data-testid={`rfx-row-${rfq?.id}`}
											>
												<TableCell className="min-w-[380px]">
													<div className="flex items-center gap-3">
														<Avatar avatar={rfq?.project?.logoPath} />
														<div className="grid">
															<p className="title font-roboto text-sm font-medium text-primary-900">
																{rfq?.name}
															</p>
															<p className="font-roboto text-sm font-normal text-primary-500">
																{rfq?.supplierList?.name}
															</p>
														</div>
													</div>
												</TableCell>
												<TableCell className={tw`max-w-[280px]`}>
													<p
														className="text line-clamp-2"
														dangerouslySetInnerHTML={{
															__html: rfq?.description,
														}}
													/>
												</TableCell>
												<TableCell className={tw`w-[120px]`}>
													<p className="text">
														<VStack>
															<StatusBadge status={rfq?.status} />
															<UnreadMessage onClick={(e) => {
																e.stopPropagation();
																navigate(clientPaths.viewRfx(rfq, RFX_TABS.COMMS));
															}} value={rfq?.unreadMessages} />
														</VStack>
													</p>
												</TableCell>
												<TableCell className={tw`max-w-[218px]`}>
													<p className="text font-semibold">
														{rfq?.submissionDeadline
															? dayjs(rfq?.submissionDeadline)
																.tz(
																	timezoneChecker(
																		rfq?.submissionTimezone || "AWST"
																	)
																)
																.format("dddd DD MMMM YYYY")
															: "-"}
													</p>
													<p className="text text-gray-400">
														{rfq?.submissionDeadline
															? dayjs(rfq?.submissionDeadline)
																.tz(
																	timezoneChecker(
																		rfq?.submissionTimezone || "AWST"
																	)
																)
																.format("HH:mm a ([GMT] Z)")
															: "-"}
													</p>
												</TableCell>
												<TableCell className={tw`max-w-[250px]`}>
													<Flex
														gap={2}
														align={"center"}
													>
														{rfq?.supplierInvited?.all?.length > 0 ? (
															<>
																<p className="text text-sm font-normal text-gray-400">
																	{rfq?.supplierInvited?.all?.length} users
																</p>
																<GlobalAvatarGroup
																	maxAvatars={2}
																	avatarSize="sm"
																	avatars={
																		rfq?.supplierInvited?.all?.length > 0
																			? rfq?.supplierInvited?.all?.map(
																				(company) => ({
																					avatar: company?.company?.logo_path,
																				})
																			)
																			: []
																	}
																/>
															</>
														) : (
															<div
																style={{
																	border: "solid 2px #F2F4F7",
																	fontSize: "12px",
																	textAlign: "center",
																	fontWeight: 500,
																	width: "28px",
																	height: "28px",
																	marginLeft: "8px",
																}}
																className="rounded-full pt-0.5"
															>
																{"-"}
															</div>
														)}
													</Flex>
												</TableCell>
												<TableCell className={tw`max-w-[218px]`}>
													<ProgressCircular
														detail={all > 0 ? `${accepted} / ${all}` : "-"}
														progress={all > 0 ? (accepted / all) * 100 : 0}
														className="-translate-y-1/4"
														half
														size="xs"
													/>
												</TableCell>
												<TableCell className={tw`max-w-[218px]`}>
													<ProgressCircular
														detail={
															accepted > 0 ? `${submitted} / ${accepted}` : "-"
														}
														progress={
															submitted > 0 ? (submitted / accepted) * 100 : 0
														}
														className="-translate-y-1/4"
														half
														size="xs"
													/>
												</TableCell>
												<TableCell className={tw`max-w-[68px]`}>
													{/* TASK-027 Panel/Approvals adapter: gate on
													`rfq.supplierInvited`, same owner-scope check as
													RFQCard.js's matching comment. */}
													{rfq?.supplierInvited && (
														<div onClick={(e) => e.stopPropagation()}>
															<RfxRowActions
																rfx={rfq}
																onExtendClick={onExtendClick}
																viewHref={linkUrl}
															/>
														</div>
													)}
												</TableCell>
											</TableRow>
										);
									})}
								{isLoading &&
									currPage >= 1 &&
									rfqsData &&
									[...Array(2)].map(() => <SkeleteonRow />)}
							</>
						) : (
							<TableRow className="col-span-5 h-[500px]">
								<div className="absolute left-2/4 -translate-x-2/4">
									<NotFoundDatas
										text={`No RFxs found`}
										description={`To get started, add a New RFx.`}
									>
										{currentTab !== "archived" && (
											<Button
												style={{ maxHeight: "48px" }}
												onClick={() => {
													onOpen();
													setModalType("createRFQ");
												}}
											>
												<Flex className="items-center gap-[6px]">
													<div className="flex h-5 w-5 items-center justify-center">
														<Icon
															icon="plus"
															style={{
																width: "12px",
																height: "12px",
																color: "#fff",
																fontWeight: "bold",
															}}
														/>
													</div>
													<div className="font-roboto text-base font-semibold capitalize">
														Create RFx
													</div>
												</Flex>
											</Button>
										)}
									</NotFoundDatas>
								</div>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
		</ListViewStyled>
	);
};

export default RFQListView;
